import { ArrowBack, ArrowForward } from "@mui/icons-material";
import * as MUI from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Fragment, useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IoIosAddCircle } from "react-icons/io";
import { useParams } from "react-router-dom";
import { cancelButtonVariant } from "../../../../components/buttons/ConfirmAndCancelButtons";
import { SingleBoxContext } from "../../../../contexts/BoxProvider";
import { ADD_NEW_THERAPY } from "../../../../enums/ApiPaths";
import { ALERTS_IN_BOX, SINGLE_BOX_THERAPY } from "../../../../enums/QueryKeys";
import { useIsMobile } from "../../../../utils/Functions";
import { StepIcon } from "../../../appointments/singleView/components/utils/FinishMeetingUtils";
import useSubmit from "../../../master/species/hooks/useSubmit";
import TherapyDetails, { labels } from "./TherapyDetails";
import TherapyGeneral from "./TherapyGeneral";
import TherapyOverview from "./TherapyOverview";

export default function AddTherapy() {
  const { getPetsInBox } = useContext(SingleBoxContext);
  const petsInBox = getPetsInBox();
  const defaultPet = petsInBox?.length === 1 ? petsInBox[0] : undefined;
  const [activeStep, setActiveStep] = useState(0);
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const submittion = useSubmit(null, ADD_NEW_THERAPY);
  const methods = useForm({
    mode: "all",
    defaultValues: {
      alert: true,
      pet: defaultPet,
    },
  });

  const title = methods.watch("title");
  const pet = methods.watch("pet");
  const dates = methods.watch("dates");
  const numberOfDays = methods.watch("numberOfDays");

  const { id } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setActiveStep(0);
  };

  const handleNext = () => {
    if (activeStep < basedSteps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  function disableNextStep() {
    if (activeStep === basedSteps.length - 1) {
      return true;
    }

    if (activeStep === 0) {
      return !title || !pet;
    }

    if (activeStep === 1) {
      const hasDates = Array.isArray(dates) && dates.length > 0;
      const hasNumberOfDays = typeof numberOfDays === "number";
      const isMorningDosageEnabled = methods.watch(
        labels.dosageMorningCheckbox,
      );
      const morningDosageValue = methods.watch(labels.dosageMorning);
      const isEveningDosageEnabled = methods.watch(
        labels.dosageEveningCheckbox,
      );
      const eveningDosageValue = methods.watch(labels.dosageEvening);

      const isMorningDosageMissing =
        isMorningDosageEnabled && !morningDosageValue;
      const isEveningDosageMissing =
        isEveningDosageEnabled && !eveningDosageValue;

      if (isMorningDosageMissing || isEveningDosageMissing) {
        return true;
      }

      return !hasDates && !hasNumberOfDays;
    }

    return false;
  }

  async function handleSubmitTherapy() {
    const values = methods.getValues();

    const submitObj = {
      boxId: id,
      title: values?.title,
      description: values?.description,
      petId: values?.pet?.id,
      administration: values?.administration,
      numberOfDays: values?.numberOfDays,
      dateList: values?.dates,
      alert: values?.alert,
      morningDoses: values?.morningDoses ?? null,
      eveningDoses: values?.eveningDoses ?? null,
    };

    const res = await submittion.submitCallback(submitObj);

    if (res) {
      await queryClient.invalidateQueries({
        queryKey: [SINGLE_BOX_THERAPY, id],
      });
      queryClient.invalidateQueries([ALERTS_IN_BOX + id]);
      methods.reset();
      handleCloseDialog();
    }
  }
  useEffect(() => {
    if (!open) return;

    if (petsInBox?.length === 1) {
      methods.setValue("pet", petsInBox[0], { shouldValidate: true });
    }
  }, [petsInBox, open, methods]);

  return (
    <Fragment>
      {isMobile ? (
        <MUI.IconButton
          onClick={() => handleOpenDialog()}
          color="primary"
          title={t("AddTherapy")}
        >
          <IoIosAddCircle />
        </MUI.IconButton>
      ) : (
        <MUI.Button variant="contained" onClick={() => handleOpenDialog()}>
          {t("AddTherapy")}
        </MUI.Button>
      )}
      <MUI.Dialog open={open} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <FormProvider {...methods}>
          <MUI.DialogContent
            sx={{
              minHeight: 750,
              px: isMobile ? 0.25 : 3,
            }}
          >
            <MUI.Stepper activeStep={activeStep} alternativeLabel>
              {basedSteps.map((label, index) => (
                <MUI.Step key={index}>
                  <MUI.StepLabel StepIconComponent={StepIcon}>
                    {t(label.title)}
                  </MUI.StepLabel>
                </MUI.Step>
              ))}
            </MUI.Stepper>

            <MUI.Container sx={{ mt: 5, mb: 3 }}>
              {basedSteps[activeStep].body}
            </MUI.Container>
          </MUI.DialogContent>
        </FormProvider>

        <MUI.DialogActions sx={{ px: 4, pb: 3, mt: 4 }}>
          <MUI.Stack spacing={isMobile ? 4 : 2} width="100%">
            {isMobile ? (
              <MUI.Stack
                direction={"row"}
                justifyContent={"center"}
                alignItems={"center"}
                mb={3}
              >
                <MUI.IconButton
                  onClick={handleBack}
                  disabled={activeStep === 0}
                  variant="contained"
                  title={t("Back")}
                >
                  <ArrowBack fontSize="large" />
                </MUI.IconButton>

                <MUI.IconButton
                  onClick={handleNext}
                  variant="contained"
                  disabled={disableNextStep()}
                  sx={{ ml: 1 }}
                  title={t("Forward")}
                >
                  <ArrowForward fontSize="large" />
                </MUI.IconButton>
              </MUI.Stack>
            ) : null}
            <MUI.Stack
              direction="row"
              alignItems="center"
              justifyContent={"space-between"}
              width="100%"
            >
              <MUI.Button
                color="error"
                variant={cancelButtonVariant}
                onClick={handleCloseDialog}
              >
                {t("Close")}
              </MUI.Button>

              {isMobile ? null : (
                <MUI.Box>
                  <MUI.IconButton
                    onClick={handleBack}
                    disabled={activeStep === 0}
                    variant="contained"
                    title={t("Back")}
                  >
                    <ArrowBack />
                  </MUI.IconButton>

                  <MUI.IconButton
                    onClick={handleNext}
                    variant="contained"
                    disabled={disableNextStep()}
                    sx={{ ml: 1 }}
                    title={t("Forward")}
                  >
                    <ArrowForward />
                  </MUI.IconButton>
                </MUI.Box>
              )}

              <MUI.Button
                onClick={() => handleSubmitTherapy()}
                variant="contained"
                disabled={activeStep !== basedSteps.length - 1}
                sx={{ ml: 1 }}
              >
                {t("Create")}
              </MUI.Button>
            </MUI.Stack>
          </MUI.Stack>
        </MUI.DialogActions>
      </MUI.Dialog>
    </Fragment>
  );
}

const basedSteps = [
  {
    title: "DoctorsLetter",
    body: <TherapyGeneral />,
  },
  {
    title: "Statistics",
    body: <TherapyDetails />,
  },
  {
    title: "Overview",
    body: <TherapyOverview />,
  },
];
